import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as usersApi from './users.api';

import type {
  CreateUserRequest,
  UpdateUserRequest,
  UserEmailLogListQuery,
  UserListQuery,
  UserRole,
} from '@utazzo/shared';

const QK = {
  list: (query: UserListQuery) => ['users', 'list', query] as const,
  detail: (id: string) => ['users', 'detail', id] as const,
};

export const useUsersList = (query: UserListQuery) =>
  useQuery({
    queryKey: QK.list(query),
    queryFn: () => usersApi.listUsers(query),
    staleTime: 30_000,
  });

export const useUserDetail = (id: string | null) =>
  useQuery({
    queryKey: QK.detail(id ?? ''),
    queryFn: () => usersApi.getUser(id!),
    enabled: !!id,
  });

/** Role options for the user form. Server hides the ADMIN role for finance. */
export const useAssignableRoles = () =>
  useQuery({
    queryKey: ['users', 'assignable-roles'] as const,
    queryFn: () => usersApi.listAssignableRoles(),
    staleTime: 60_000,
  });

export const useCreateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateUserRequest) => usersApi.createUser(payload),
    onSuccess: (user) => {
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      toast.success(`Created ${user.email}. A password reset link has been issued.`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create user'),
  });
};

export const useUpdateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateUserRequest }) =>
      usersApi.updateUser(id, payload),
    onSuccess: (user) => {
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(user.id) });
      toast.success('User updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update user'),
  });
};

export const useDeactivateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.deactivateUser(id),
    onSuccess: (user) => {
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(user.id) });
      toast.success('User deactivated. Any live sessions were ended.');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to deactivate user'),
  });
};

export const useReactivateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.reactivateUser(id),
    onSuccess: (user) => {
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      qc.invalidateQueries({ queryKey: QK.detail(user.id) });
      toast.success('User reactivated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to reactivate user'),
  });
};

export const useSendPasswordReset = () =>
  useMutation({
    mutationFn: (id: string) => usersApi.sendPasswordResetForUser(id),
    onSuccess: () => toast.success('Password reset link issued'),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to send reset link'),
  });

export const useDeleteUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => usersApi.deleteUser(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      toast.success('User deleted');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete user'),
  });
};

export const useBulkDeleteUsers = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => usersApi.bulkDeleteUsers(ids),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['users', 'list'] });
      toast.success(`Deleted ${res.deleted} ${res.deleted === 1 ? 'user' : 'users'}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to delete users'),
  });
};

export const useBulkSetUsersStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, isActive }: { ids: string[]; isActive: boolean }) =>
      usersApi.bulkSetUsersStatus(ids, isActive),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users', 'list'] }),
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update user status'),
  });
};

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};

export const extractApiMessage = extractMessage;

/// Reporting-manager candidates for the role currently picked in the
/// user form. Re-fires when role changes. Disabled when role is null
/// (form not yet ready) or ADMIN (the root role has no manager).
export const useEligibleManagers = (role: UserRole | null | undefined) =>
  useQuery({
    queryKey: ['users', 'eligible-managers', role ?? ''] as const,
    queryFn: () => usersApi.listEligibleManagers(role!),
    enabled: !!role && role !== 'ADMIN',
    staleTime: 0,
    retry: 1,
  });

/// Org-chart payload for a single user (drives the Organization dialog
/// on the users list).
export const useUserHierarchy = (id: string | null | undefined) =>
  useQuery({
    queryKey: ['users', 'hierarchy', id ?? ''] as const,
    queryFn: () => usersApi.getUserHierarchy(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

/// Email log for one user. Drives the "Email log" tab on the
/// user-detail page.
export const useUserEmailLogs = (id: string | null | undefined, query: UserEmailLogListQuery) =>
  useQuery({
    queryKey: ['users', 'email-logs', id ?? '', query] as const,
    queryFn: () => usersApi.listUserEmailLogs(id!, query),
    enabled: !!id,
    staleTime: 30_000,
  });

/// Per-user permission snapshot (role baseline + extras). 30s stale
/// because the role baseline rarely changes during a session.
export const useUserPermissions = (id: string | null) =>
  useQuery({
    queryKey: ['users', 'permissions', id ?? ''] as const,
    queryFn: () => usersApi.getUserPermissions(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

/// Replace the user's EXTRA grants. Invalidates the snapshot so the
/// matrix re-renders with the server-canonical state (which has
/// already filtered out baseline-duplicate codes).
export const useSetUserPermissions = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, codes }: { id: string; codes: string[] }) =>
      usersApi.setUserPermissions(id, { codes }),
    onSuccess: (snap) => {
      qc.setQueryData(['users', 'permissions', snap.userId], snap);
      qc.invalidateQueries({ queryKey: ['users', 'permissions', snap.userId] });
      toast.success('Permissions updated');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to update permissions'),
  });
};

/// Per-user Marketing Reports field visibility (the catalog keys hidden from
/// this user). Separate from permissions - it is a "hide" list, not a grant.
export const useUserMarketingFieldVisibility = (id: string | null) =>
  useQuery({
    queryKey: ['users', 'marketing-field-visibility', id ?? ''] as const,
    queryFn: () => usersApi.getUserMarketingFieldVisibility(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

export const useSetUserMarketingFieldVisibility = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, hidden }: { id: string; hidden: string[] }) =>
      usersApi.setUserMarketingFieldVisibility(id, hidden),
    onSuccess: (hidden, { id }) => {
      qc.setQueryData(['users', 'marketing-field-visibility', id], hidden);
      qc.invalidateQueries({ queryKey: ['users', 'marketing-field-visibility', id] });
      toast.success('Marketing Reports visibility updated');
    },
    onError: (err: unknown) =>
      toast.error(extractMessage(err) ?? 'Failed to update field visibility'),
  });
};
